import React, { memo, useState } from 'react'
import { Divider, Grid, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useStyles } from '../styles/form.style'
import { IProductWarrantyClaim, MediaCategories } from '@cbgms/api'
import { AttachmentItem } from '@cbgms/components/general/AttachmentItem'
import { ImageViewDialog } from './ImageViewDialog'
import { adhocAttachmentWholesalerUUID, convertISOToLocalDate, supportedImageFileTypes } from '../utils'
import { useFormikContext } from 'formik'
import { useDispatch } from 'react-redux'
import { useBase64BlobUrl } from 'app-support/media/hooks/use-build-base64-blob-url'
import { SupportMediaParams } from 'app-support/media/state/request'
import { getPrivateMediaItem } from 'app-support/media/state/actions'

const _WarrantyDetailsViewSection = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const classes = useStyles()
  const { values: formValues } = useFormikContext<IProductWarrantyClaim>()
  const buildBlobURL = useBase64BlobUrl()
  const [showImageViewDialog, setShowImageViewDialog] = useState<boolean>(false)
  const [selectedFile, setSelectedFile] = useState<IProductWarrantyClaim['AttachmentsS3'][number]>()
  formValues.AttachmentsS3 = formValues.AttachmentsS3.filter(att => att.Key.length)
  const checkIsImageAttachment = (name: string) => supportedImageFileTypes.includes(name.split('.').pop()?.toLocaleLowerCase() || '')
  const imageAttachments = formValues.AttachmentsS3.filter(att => checkIsImageAttachment(att.Name))

  const viewFile = async (fileName: string) => {
    const fileDetails = formValues.AttachmentsS3.find(item => item.Name === fileName)
    const SupportMediaParams: SupportMediaParams = {
      category: MediaCategories.AdhocAttachments,
      companyUUID: formValues.CompanyUUID,
      subCategory: adhocAttachmentWholesalerUUID,
      fileName: fileDetails?.Name
    }
    const { Data } = await dispatch(getPrivateMediaItem(SupportMediaParams))
    if (fileDetails) {
      setSelectedFile(fileDetails)
      checkIsImageAttachment(fileName) ? setShowImageViewDialog(true) : window.open(buildBlobURL(Data.Data, Data.ContentType), '_blank')
    }
  }

  return (
    <Grid item direction='column'>
      <Grid item sx={{ mt: 1 }}>
        <Divider className={classes.widthPercentage} />
      </Grid>

      <Grid item sx={{ my: 3 }}>
        <Typography className={classes.sectionHeader}>{t('productWarrantyClaim.warranty_details')}</Typography>
      </Grid>
      <Grid item container spacing={2}>
        <Grid item xs={3}>
          <Typography fontWeight='500'>{t('productWarrantyClaim.contact_person')}:</Typography>
        </Grid>
        <Grid item xs={8}>
          <Typography>{formValues?.FormData.WarrantyDetails.ContactPerson}</Typography>
        </Grid>

        <Grid item xs={3}>
          <Typography fontWeight='500'>{t('productWarrantyClaim.purchase_date')}:</Typography>
        </Grid>
        <Grid item xs={8}>
          <Typography>{convertISOToLocalDate(formValues.FormData.WarrantyDetails.PurchaseDate)}</Typography>
        </Grid>

        <Grid item xs={3}>
          <Typography fontWeight='500'>{t('productWarrantyClaim.is_telephone_contact')}</Typography>
        </Grid>
        <Grid item xs={8}>
          <Typography>{formValues?.FormData.WarrantyDetails.IsTelephoneContact ? t('global.yes') : t('global.no')}</Typography>
        </Grid>

        <Grid item xs={3}>
          <Typography fontWeight='500'>{t('productWarrantyClaim.employee_name')}:</Typography>
        </Grid>
        <Grid item xs={8}>
          <Typography>{formValues?.FormData.WarrantyDetails.EmployeeName}</Typography>
        </Grid>

        <Grid item xs={3}>
          <Typography fontWeight='500'>{t('productWarrantyClaim.transport_damage')}</Typography>
        </Grid>
        <Grid item xs={8}>
          <Typography>{formValues?.FormData.WarrantyDetails.TransportDamage ? t('global.yes') : t('global.no')}</Typography>
        </Grid>

        <Grid item xs={3}>
          <Typography fontWeight='500'>{t('productWarrantyClaim.diagnosis_cause')}:</Typography>
        </Grid>
        <Grid item xs={8}>
          <Typography>{formValues?.FormData.WarrantyDetails.DiagnosisCause}</Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography fontWeight='500'>{t('productWarrantyClaim.chosen_solution')}:</Typography>
        </Grid>
        <Grid item xs={8}>
          <Typography>{formValues?.FormData.WarrantyDetails.ChosenSolution}</Typography>
        </Grid>
        {formValues.AttachmentsS3.map((attachment, index) => {
          return (
            <React.Fragment key={index}>
              <Grid item xs={3} alignSelf='center'>
                <Typography fontWeight='500'>
                  {t('productWarrantyClaim.attachment')} {index + 1}:
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  <Grid sx={{ display: 'inline-flex' }} key={index} item data-testid='uploaded-file-item'>
                    <AttachmentItem
                      default={false}
                      url={attachment.Name}
                      view={false}
                      handleClick={viewFile}
                      marginbottom={0}
                      allowFileRemoval={false}
                      showVisibilityIcon={false}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </React.Fragment>
          )
        })}
        {showImageViewDialog && selectedFile && (
          <ImageViewDialog
            selectedFile={selectedFile}
            companyUUID={formValues.CompanyUUID}
            onCancel={() => setShowImageViewDialog(false)}
            allImages={imageAttachments}
          />
        )}
      </Grid>
    </Grid>
  )
}

const WarrantyDetailsViewSection = memo(_WarrantyDetailsViewSection)
export default WarrantyDetailsViewSection
